import React, { useEffect, useRef, useState, KeyboardEvent } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from "@mui/material";
import { useSnackbar } from "notistack";
import api from "../../app/api";
import { calculateNewPhaseShift } from "./phaseShift";

interface ReprocessMetricRow {
  metric: string;
  cylinder: string;
  valueType: string;
  value: string;
}

interface ManualRawFileOption {
  name: string;
  path: string;
  modifiedAt: string;
  size: number;
}

interface ConfigModuleOption {
  moduleKey: string;
  moduleNumber: number;
  moduleName: string;
  processKey: string;
  processName: string;
  firingOrder: string;
  firingOrderList: string[];
  strokeType: string;
  shiftAngleMethod: string;
  shiftAngle: string;
}

const normalizeModuleKey = (value: string) => String(value || "").trim().toUpperCase();

const dedupeByKey = <T,>(items: T[], keySelector: (item: T) => string) => {
  const map = new Map<string, T>();

  items.forEach((item) => {
    const key = keySelector(item).trim().toLowerCase();
    if (!key) {
      return;
    }

    if (!map.has(key)) {
      map.set(key, item);
    }
  });

  return Array.from(map.values());
};

const formatManualRawFiles = (files: ManualRawFileOption[]) =>
  dedupeByKey(files, (file) => file.path || file.name)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const OutputValueBox = styled(Box)(({ theme }) => ({
  fontFamily: "monospace",
  fontSize: 28,
  fontWeight: 700,
  color: theme.palette.primary.main,
  background: "#fff",
  border: `1.5px solid ${theme.palette.primary.light}`,
  borderRadius: 8,
  padding: "8px 18px",
  minWidth: 90,
  textAlign: "center",
  letterSpacing: 1,
}));

const PhaseShiftContent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [mode, setMode] = useState<"manual">("manual");
  const [strokeType, setStrokeType] = useState<"2" | "4">("4");
  const [direction, setDirection] = useState<"auto" | "right" | "left">("auto");
  const [firingOrder, setFiringOrder] = useState("");
  const [faultInducedCylinder, setFaultInducedCylinder] = useState("");
  const [degreeAngle, setDegreeAngle] = useState("0");
  const [injectionAngle, setInjectionAngle] = useState("");
  const [faultType, setFaultType] = useState("");
  const [cylinders, setCylinders] = useState<string[]>([]);
  const [cylInput, setCylInput] = useState("");
  const [cylinderError, setCylinderError] = useState("");
  const [newPhaseShift, setNewPhaseShift] = useState<string>("—");
  const [manualRawFiles, setManualRawFiles] = useState<ManualRawFileOption[]>(
    [],
  );
  const [configModules, setConfigModules] = useState<ConfigModuleOption[]>([]);
  const [selectedModuleKey, setSelectedModuleKey] = useState("");
  const [selectedRawFilePath, setSelectedRawFilePath] = useState("");
  const [manualDirectory, setManualDirectory] = useState("");
  const [isLoadingRawFiles, setIsLoadingRawFiles] = useState(false);
  const [reprocessStatus, setReprocessStatus] = useState<string>("");
  const [reprocessMeta, setReprocessMeta] = useState<string>("");
  const [reprocessRows, setReprocessRows] = useState<ReprocessMetricRow[]>([]);
  const [reprocessRawOutput, setReprocessRawOutput] = useState<string>("");
  const [isReprocessing, setIsReprocessing] = useState(false);
  const computeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firingOrderValues = firingOrder
    .split(/[\s,]+/)
    .map((value) => value.trim())
    .filter(Boolean);
  const isFaultCylinderInvalid =
    faultInducedCylinder.trim() !== "" &&
    !firingOrderValues.some(
      (value) =>
        value.toUpperCase() === faultInducedCylinder.trim().toUpperCase(),
    );

  useEffect(() => {
    let isMounted = true;

    const loadRawFiles = async () => {
      setIsLoadingRawFiles(true);

      try {
        const response = await api.get("/tools/reprocess-vib360-options.php");
        const files = formatManualRawFiles(response?.data?.data?.files || []);
        const modules = dedupeByKey(
          response?.data?.data?.modules || [],
          (module: ConfigModuleOption) => module.moduleKey || module.moduleName || "",
        );
        const directory = String(response?.data?.data?.directory || "");

        if (!isMounted) {
          return;
        }

        setManualRawFiles(files);
        setConfigModules(modules as ConfigModuleOption[]);
        setManualDirectory(directory);
        setSelectedRawFilePath((currentValue) => {
          if (
            currentValue &&
            files.some(
              (file: ManualRawFileOption) => file.path === currentValue,
            )
          ) {
            return currentValue;
          }

          return files[0]?.path || "";
        });
        setSelectedModuleKey((currentValue) => {
          if (
            currentValue &&
            modules.some(
              (module: ConfigModuleOption) =>
                normalizeModuleKey(module.moduleKey) ===
                normalizeModuleKey(currentValue),
            )
          ) {
            return currentValue;
          }

          return modules[0]?.moduleKey ? normalizeModuleKey(modules[0].moduleKey) : "";
        });
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        const message =
          error?.Message ||
          error?.message ||
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          (error?.status
            ? `Failed to load MANUAL raw files (${error.status})`
            : "Failed to load MANUAL raw files");
        setReprocessStatus(message);
      } finally {
        if (isMounted) {
          setIsLoadingRawFiles(false);
        }
      }
    };

    loadRawFiles();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const selectedModule = configModules.find(
      (module) => normalizeModuleKey(module.moduleKey) === normalizeModuleKey(selectedModuleKey),
    );
    if (!selectedModule) {
      return;
    }

    const resolvedFiringOrder =
      selectedModule.firingOrder || selectedModule.firingOrderList?.join(",") || "";
    setFiringOrder(resolvedFiringOrder);
    if (
      selectedModule.strokeType === "2" ||
      selectedModule.strokeType === "4"
    ) {
      setStrokeType(selectedModule.strokeType as "2" | "4");
    }
  }, [configModules, selectedModuleKey]);

  const handleCylKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === ",") && cylInput.trim()) {
      e.preventDefault();
      const val = cylInput.replace(",", "").trim();
      const order = firingOrder
        .split(/[\s,]+/)
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean);
      const isValidCylinder = order.includes(val.toUpperCase());

      if (!isValidCylinder) {
        setCylinderError(
          `Invalid cylinder. Enter one of: ${order.join(", ") || "the firing order"}.`,
        );
        setCylInput("");
        return;
      }

      setCylinderError("");
      if (val && !cylinders.some((cylinder) => cylinder.toUpperCase() === val.toUpperCase()))
        setCylinders((prev) => [...prev, val]);
      setCylInput("");
    } else if (e.key === "Backspace" && !cylInput && cylinders.length) {
      setCylinders((prev) => prev.slice(0, -1));
    }
  };

  const compute = () => {
    if (computeTimeoutRef.current) {
      clearTimeout(computeTimeoutRef.current);
    }

    setNewPhaseShift("···");
    computeTimeoutRef.current = setTimeout(() => {
      const detectedCylinder = cylInput.trim() || cylinders[cylinders.length - 1] || "";
      const result = calculateNewPhaseShift({
        injectionAngle,
        degreeAngle,
        faultInducedCylinder,
        detectedCylinder,
        firingOrder,
        engineStroke: strokeType,
      });

      if (!result.ok) {
        setNewPhaseShift("ERR");
        setCylinderError(result.error || "Enter a valid fault cylinder from the firing order.");
        return;
      }

      setCylinderError("");
      setNewPhaseShift(`${(result.value ?? 0).toFixed(1)}`);
    }, 600);
  };

  useEffect(() => {
    const detectedCylinder = cylInput.trim() || cylinders[cylinders.length - 1] || "";
    if (
      (injectionAngle.trim() || detectedCylinder) &&
      degreeAngle.trim() &&
      faultInducedCylinder.trim() &&
      firingOrder.trim()
    ) {
      compute();
    } else {
      setNewPhaseShift("—");
    }
  }, [
    injectionAngle,
    degreeAngle,
    faultInducedCylinder,
    cylInput,
    cylinders,
    firingOrder,
    strokeType,
  ]);

  const extractJsonFromOutput = (output: string) => {
    const start = output.indexOf("{");
    const end = output.lastIndexOf("}");
    if (start < 0 || end <= start) return null;

    try {
      return JSON.parse(output.slice(start, end + 1));
    } catch {
      return null;
    }
  };

  const toMetricRows = (payload: any): ReprocessMetricRow[] => {
    const keys = Object.keys(payload || {});
    if (!keys.length) return [];

    const timeKey = keys[0];
    const diagData = payload[timeKey] || {};

    const rows: ReprocessMetricRow[] = [];
    const status = Number(diagData?.Status);

    const engine = diagData.Engine || {};
    const firingOrder = Array.isArray(engine.FiringOrder)
      ? engine.FiringOrder
      : [];
    const cylinderName = (idx: number) =>
      firingOrder[idx] !== undefined
        ? `Cyl ${firingOrder[idx]}`
        : `Cyl ${idx + 1}`;

    const pushCylinderRows = (
      metric: string,
      valueType: string,
      values: any,
    ) => {
      if (!Array.isArray(values)) return;
      values.forEach((val, idx) => {
        rows.push({
          metric,
          cylinder: cylinderName(idx),
          valueType,
          value: String(val ?? "-"),
        });
      });
    };

    pushCylinderRows(
      "Injection Condition",
      "Cylinder Health",
      diagData?.InjectionCondition?.cylinderHealth,
    );
    pushCylinderRows(
      "Compression",
      "Cylinder Health",
      diagData?.Compression?.cylinderHealth,
    );
    if (status === 3) {
      pushCylinderRows(
        "Peak Pressure",
        "Cylinder Value",
        diagData?.Pressure?.cylinderValues,
      );
    }
    pushCylinderRows(
      "Bearing",
      "Cylinder Health",
      diagData?.Bearing?.cylinderHealth,
    );
    pushCylinderRows(
      "Cylinder Moving Part",
      "Cylinder Health",
      diagData?.BearingBis?.cylinderHealth,
    );

    const processName = engine.ProcessName || engine.Name || "-";
    const processId = engine.Id || "-";
    const channelSpeed = diagData.ChannelSpeed || "-";
    setReprocessMeta(
      `Date: ${timeKey} | Process: ${processName} (${processId}) | Speed: ${channelSpeed}`,
    );

    return rows;
  };

  const handleReprocess = async () => {
    if (!selectedRawFilePath) {
      const message = "Select a MANUAL raw file";
      setReprocessStatus(message);
      enqueueSnackbar({ message, variant: "warning" });
      return;
    }

    if (!selectedModuleKey) {
      const message = "Select a module from configuration";
      setReprocessStatus(message);
      enqueueSnackbar({ message, variant: "warning" });
      return;
    }

    const numericPhaseShift = parseFloat(newPhaseShift);
    if (!Number.isFinite(numericPhaseShift)) {
      const message = "Calculate a valid new phase shift first";
      setReprocessStatus(message);
      enqueueSnackbar({ message, variant: "warning" });
      return;
    }

    setIsReprocessing(true);
    setReprocessStatus("Reprocessing...");

    try {
      const formData = new FormData();
      formData.append("rawFilePath", selectedRawFilePath);
      formData.append("moduleKey", selectedModuleKey);
      formData.append("newPhaseShift", String(numericPhaseShift));

      const apiUrl = process.env.REACT_APP_API_URL || "";
      if (apiUrl) {
        try {
          const host = new URL(apiUrl).hostname;
          formData.append("sshHost", host);
        } catch (e) {
          // ignore malformed URL and let backend use default
        }
      }

      const response = await api.post("/tools/reprocess-vib360.php", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const output = String(response?.data?.data?.output || "");
      const configFile = String(response?.data?.data?.configFile || "");
      const binaryPath = String(response?.data?.data?.binary || "");
      const updatedPhaseShift = String(
        response?.data?.data?.updatedPhaseShift || "",
      );
      setReprocessRawOutput(
        output.trim()
          ? output.trim().slice(-8000)
          : "No stdout or stderr output was returned.",
      );

      const summaryRows = response?.data?.data?.diagSummary?.rows;
      const summaryTimestamp = String(
        response?.data?.data?.diagSummary?.timestamp || "",
      );
      const summaryProcessName = String(
        response?.data?.data?.diagSummary?.processName || "",
      );
      const summaryProcessId = String(
        response?.data?.data?.diagSummary?.processId || "",
      );
      const summarySpeed = String(
        response?.data?.data?.diagSummary?.channelSpeed || "",
      );
      let parsedRows: ReprocessMetricRow[] = [];

      if (Array.isArray(summaryRows) && summaryRows.length) {
        parsedRows = summaryRows as ReprocessMetricRow[];
        setReprocessRows(parsedRows);
        setReprocessMeta(
          `Date: ${summaryTimestamp} | Process: ${summaryProcessName} (${summaryProcessId}) | Speed: ${summarySpeed}`,
        );
      } else {
        const parsedFromApi = response?.data?.data?.lastDiagJson;
        const parsed =
          parsedFromApi && typeof parsedFromApi === "object"
            ? parsedFromApi
            : extractJsonFromOutput(output);
        parsedRows = parsed ? toMetricRows(parsed) : [];
        setReprocessRows(parsedRows);
      }

      if (parsedRows.length > 0) {
        const statusMsg = `Updated phase shift to ${updatedPhaseShift} | Reprocess completed and parsed | Binary: ${binaryPath || "unknown"}${configFile ? ` | Config copy: ${configFile}` : ""}`;
        setReprocessStatus(statusMsg);
        enqueueSnackbar({
          message: `✓ Reprocess completed successfully. Phase shift updated to ${updatedPhaseShift}.`,
          variant: "success",
        });
      } else {
        setReprocessMeta("");
        const statusMsg = `Updated phase shift to ${updatedPhaseShift} | Reprocess completed but no diagnostic metric rows were found | Binary: ${binaryPath || "unknown"}${configFile ? ` | Config copy: ${configFile}` : ""}`;
        setReprocessStatus(statusMsg);
        enqueueSnackbar({
          message: "⚠ Reprocess completed but no diagnostic metric rows were found.",
          variant: "warning",
        });
      }
    } catch (error: any) {
      const message = error?.Message || error?.message || "Reprocess failed";
      setReprocessRows([]);
      setReprocessMeta("");
      setReprocessStatus(message);
      enqueueSnackbar({
        message: `✗ Reprocess failed: ${message}`,
        variant: "error",
      });
    } finally {
      setIsReprocessing(false);
    }
  };

  const metricDisplayOrder = [
    "Injection Condition",
    "Compression",
    "Peak Pressure",
    "Bearing",
    "Cylinder Moving Part",
  ];
  const matrixCylinders = Array.from(new Set(reprocessRows.map((r) => r.cylinder)));
  const matrixMetricMap = reprocessRows.reduce(
    (acc, row) => {
      if (!acc[row.metric]) {
        acc[row.metric] = {};
      }
      acc[row.metric][row.cylinder] = row.value;
      return acc;
    },
    {} as Record<string, Record<string, string>>,
  );
  const matrixMetrics = metricDisplayOrder.filter(
    (metric) => matrixMetricMap[metric],
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
        Phase Shift
      </Typography>

      {/* Row 1 - Top Controls */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Column 1: Mode */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "text.secondary",
              display: "block",
              mb: 0.75,
              fontSize: "0.7rem",
            }}
          >
            Mode
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={(_, v) => v && setMode(v)}
            size="small"
            fullWidth
            sx={{ height: 36 }}
          >
            <ToggleButton
              value="manual"
              sx={{
                fontWeight: 600,
                fontFamily: "monospace",
                fontSize: "0.9rem",
              }}
            >
              Manual
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Column 3: Degree Angle */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "text.secondary",
              display: "block",
              mb: 0.75,
              fontSize: "0.7rem",
            }}
          >
            Current Phase Angle
          </Typography>
          <TextField
            placeholder="0"
            type="number"
            value={degreeAngle}
            onChange={(e) => setDegreeAngle(e.target.value)}
            size="small"
            fullWidth
            inputProps={{
              step: 0.1,
              max: strokeType === "4" ? 720 : 360,
              style: {
                fontFamily: "monospace",
                fontWeight: 600,
                fontSize: "0.9rem",
              },
            }}
            sx={{
              height: 36,
              "& .MuiOutlinedInput-root": { height: 36 },
            }}
          />
        </Box>

        {/* Column 2: Module */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "text.secondary",
              display: "block",
              mb: 0.75,
              fontSize: "0.7rem",
            }}
          >
            Module
          </Typography>
          <Select
            value={selectedModuleKey}
              onChange={(e) => setSelectedModuleKey(normalizeModuleKey(e.target.value))}
            displayEmpty
            size="small"
            fullWidth
            sx={{
              height: 36,
              backgroundColor: "#fff4db",
              border: "1px solid #ffcc80",
              borderRadius: 1,
              "& .MuiSelect-select": {
                fontFamily: "monospace",
                fontWeight: 700,
                fontSize: "0.9rem",
              },
            }}
          >
            {configModules.map((module) => (
              <MenuItem key={module.moduleKey} value={normalizeModuleKey(module.moduleKey)}>
                {`${module.processKey} - ${module.moduleName}`}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* Input Parameters Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          background: "#fafafa",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            mb: 2,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: "text.secondary",
            fontSize: "0.85rem",
          }}
        >
          Input Parameters
        </Typography>

        {/* Input Row: Three-column layout */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          {/* Column 1: Injection Angle & Direction */}
          <Box>
            <TextField
              label="Injection Angle"
              placeholder="e.g. 12.5"
              type="number"
              value={injectionAngle}
              onChange={(e) => setInjectionAngle(e.target.value)}
              size="small"
              fullWidth
              inputProps={{
                step: 0.1,
                style: { fontFamily: "monospace", fontWeight: 600 },
              }}
              sx={{ mb: 1.5 }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Shift Direction</InputLabel>
              <Select
                value={direction}
                label="Shift Direction"
                onChange={(e) =>
                  setDirection(e.target.value as "auto" | "right" | "left")
                }
              >
                <MenuItem value="auto">Auto</MenuItem>
                <MenuItem value="right">Right</MenuItem>
                <MenuItem value="left">Left</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Column 2: Engine Stroke & Fault Induced Cylinder */}
          <Box>
            <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
              <InputLabel>Engine Stroke</InputLabel>
              <Select
                value={strokeType}
                label="Engine Stroke"
                onChange={(e) => setStrokeType(e.target.value as "2" | "4")}
              >
                <MenuItem value="2">2-Stroke (360°)</MenuItem>
                <MenuItem value="4">4-Stroke (720°)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Fault Induced Cylinder"
              placeholder="e.g. 3 or A"
              type="text"
              value={faultInducedCylinder}
              onChange={(e) => {
                setFaultInducedCylinder(e.target.value);
                setCylinderError("");
              }}
              size="small"
              fullWidth
              error={isFaultCylinderInvalid}
              helperText={
                isFaultCylinderInvalid
                  ? `Enter one of: ${firingOrderValues.join(", ") || "the firing order"}.`
                  : " "
              }
              inputProps={{
                style: { fontFamily: "monospace", fontWeight: 600 },
              }}
            />
          </Box>

          {/* Column 3: Firing Order (read-only) */}
          <Box>
            <TextField
              label="Firing Order"
              placeholder="Auto-filled"
              value={firingOrder}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              inputProps={{
                style: {
                  fontFamily: "monospace",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                },
              }}
              sx={{
                mb: 1.5,
                "& .MuiInputBase-input": {
                  backgroundColor: "#f0f0f0",
                  fontWeight: 700,
                },
              }}
            />
            <Box
              sx={{
                height: 40,
                display: "flex",
                alignItems: "center",
                color: "text.secondary",
                fontSize: "0.8rem",
                fontStyle: "italic",
              }}
            >
              From selected module
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Detected Fault Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          background: "#f8f9ff",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            mb: 1.5,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: "text.secondary",
          }}
        >
          Cylinder Detected Fault
        </Typography>
        <Typography
          variant="caption"
          sx={{ display: "block", mb: 1.5, color: "text.secondary" }}
        >
          Enter cylinder number or letter where fault was detected (press Enter to add)
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            cursor: "text",
            borderRadius: 1.5,
            backgroundColor: "#fff",
          }}
        >
          {cylinders.map((c, i) => (
            <Chip
              key={i}
              label={`Cyl ${c}`}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() =>
                setCylinders((prev) => prev.filter((_, j) => j !== i))
              }
              sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}
            />
          ))}
          <input
            value={cylInput}
            onChange={(e) => setCylInput(e.target.value)}
            onKeyDown={handleCylKeyDown}
            placeholder={
              cylinders.length
                ? "Add more..."
                : "Type cylinder (e.g. 1, 3, A, B)"
            }
            style={{
              border: "none",
              outline: "none",
              fontFamily: "monospace",
              fontSize: 13,
              background: "transparent",
              flex: 1,
              minWidth: 140,
              padding: "6px 4px",
            }}
          />

          {cylinderError && (
            <Typography
              color="error"
              variant="caption"
              sx={{ flexBasis: "100%", fontWeight: 600 }}
            >
              {cylinderError}
            </Typography>
          )}

          <Button
            variant="contained"
            onClick={compute}
            size="small"
            sx={{
              whiteSpace: "nowrap",
              fontWeight: 500,
              px: 4,
              py: 1.2,
              fontSize: "1rem",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            Get New Phase Shift →
          </Button>
        </Paper>
        {/* Action Section */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}></Box>
      </Paper>

      {/* Output & Reprocess Section */}
      <Paper
        elevation={0}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "250px 1fr" },
          alignItems: "start",
          gap: 3,
          p: 3,
          borderRadius: 2,
          background: "linear-gradient(135deg, #eef0fd 0%, #f5f7ff 100%)",
          border: "2px solid #d0d8f2",
          mb: 2.5,
        }}
      >
        {/* New Phase Shift Output */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "primary.main",
              fontSize: "0.75rem",
            }}
          >
            New Phase Shift
          </Typography>
          <TextField
            label="Final Value"
            size="small"
            value={newPhaseShift}
            onChange={(e) => setNewPhaseShift(e.target.value)}
            fullWidth
            sx={{
              background: "#fff",
              borderRadius: 1.5,
              "& .MuiOutlinedInput-root": {
                fontFamily: "monospace",
                fontWeight: 700,
                fontSize: "1.75rem",
                textAlign: "center",
              },
            }}
            inputProps={{
              style: {
                fontFamily: "monospace",
                fontWeight: 700,
                fontSize: "1.75rem",
                textAlign: "center",
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            Editable before reprocess
          </Typography>
        </Box>

        {/* Raw File & Reprocess */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "text.secondary",
              fontSize: "0.75rem",
            }}
          >
            🔄 Reprocess Configuration
          </Typography>

          {/* Raw File Selector */}
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>Raw File From MANUAL</InputLabel>
            <Select
              value={selectedRawFilePath}
              label="Raw File From MANUAL"
              onChange={(e) => setSelectedRawFilePath(e.target.value)}
              disabled={isLoadingRawFiles || manualRawFiles.length === 0}
              sx={{ background: "#fff" }}
            >
              {manualRawFiles.map((file) => (
                <MenuItem key={file.path} value={file.path}>
                  {file.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Path Information */}
          {manualDirectory && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1,
                fontFamily: "monospace",
                color: "text.secondary",
                fontSize: "0.7rem",
                wordBreak: "break-all",
              }}
            >
              📁 {manualDirectory}
            </Typography>
          )}

          {selectedRawFilePath && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 2,
                fontFamily: "monospace",
                color: "primary.main",
                fontWeight: 600,
                fontSize: "0.75rem",
                wordBreak: "break-all",
              }}
            >
              ✓ {selectedRawFilePath.split("/").pop()}
            </Typography>
          )}

          {/* Reprocess Button & Status */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={handleReprocess}
              disabled={isReprocessing}
              sx={{
                whiteSpace: "nowrap",
                fontWeight: 700,
                flex: { xs: 1, sm: "0 1 auto" },
                py: 1,
              }}
            >
              {isReprocessing ? "⏳ Reprocessing..." : "Update + Reprocess"}
            </Button>
            {reprocessStatus && (
              <Box
                sx={{
                  flex: 1,
                  p: 1.5,
                  backgroundColor:
                    reprocessStatus.includes("ERR") ||
                    reprocessStatus.includes("Failed")
                      ? "rgba(244, 67, 54, 0.1)"
                      : reprocessStatus.includes("no diagnostic metric rows")
                        ? "rgba(255, 193, 7, 0.1)"
                        : "rgba(76, 175, 80, 0.1)",
                  borderRadius: 1,
                  border:
                    reprocessStatus.includes("ERR") ||
                    reprocessStatus.includes("Failed")
                      ? "1px solid #f44336"
                      : reprocessStatus.includes("no diagnostic metric rows")
                        ? "1px solid #ffc107"
                        : "1px solid #4caf50",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    color:
                      reprocessStatus.includes("ERR") ||
                      reprocessStatus.includes("Failed")
                        ? "error.main"
                        : reprocessStatus.includes("no diagnostic metric rows")
                          ? "warning.main"
                          : "success.main",
                    fontSize: "0.75rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {reprocessStatus}
                </Typography>
              </Box>
            )}
          </Stack>
          {reprocessRawOutput && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 0.5, fontWeight: 700 }}
              >
                Process output
              </Typography>
              <Box
                component="pre"
                sx={{
                  maxHeight: 180,
                  overflow: "auto",
                  m: 0,
                  p: 1,
                  bgcolor: "#f4f5f6",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: "0.7rem",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {reprocessRawOutput}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {reprocessRows.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Reprocess Result
          </Typography>

          {reprocessMeta && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1.5,
                fontFamily: "monospace",
                color: "text.secondary",
              }}
            >
              {reprocessMeta}
            </Typography>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                  {matrixCylinders.map((cylinder) => (
                    <TableCell
                      key={cylinder}
                      sx={{ fontWeight: 700, fontFamily: "monospace" }}
                    >
                      {cylinder}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixMetrics.map((metric) => (
                  <TableRow key={metric}>
                    <TableCell sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                      {metric}
                    </TableCell>
                    {matrixCylinders.map((cylinder) => (
                      <TableCell key={`${metric}-${cylinder}`} sx={{ fontFamily: "monospace" }}>
                        {matrixMetricMap[metric]?.[cylinder] ?? "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

// ── Peak Pressure content ─────────────────────────────────────────────────────
const PeakPressureContent = () => {
  const [currentSensitivity, setCurrentSensitivity] = useState("");
  const [vib360Value, setVib360Value] = useState("");
  const [manualValue, setManualValue] = useState("");
  const [newSensitivity, setNewSensitivity] = useState<string>("—");

  const compute = () => {
    setNewSensitivity("···");
    setTimeout(() => {
      if (currentSensitivity && vib360Value && manualValue) {
        const curr = parseFloat(currentSensitivity);
        const vib = parseFloat(vib360Value);
        const man = parseFloat(manualValue);
        if (man !== 0 && !isNaN(curr) && !isNaN(vib) && !isNaN(man)) {
          const result = (curr * vib) / man;
          setNewSensitivity(result.toFixed(4));
        } else {
          setNewSensitivity("ERR");
        }
      } else {
        setNewSensitivity("—");
      }
    }, 600);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
        Peak Pressure
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 3,
          mb: 2.5,
        }}
      >
        <TextField
          label="Current Config Sensitivity"
          placeholder="e.g. 10.5"
          type="number"
          value={currentSensitivity}
          onChange={(e) => setCurrentSensitivity(e.target.value)}
          size="small"
          fullWidth
          inputProps={{ step: 0.1, style: { fontFamily: "monospace" } }}
        />

        <TextField
          label="Vib360 Peak Pressure Measured Value"
          placeholder="e.g. 150"
          type="number"
          value={vib360Value}
          onChange={(e) => setVib360Value(e.target.value)}
          size="small"
          fullWidth
          helperText="Cylinder where sensor is installed"
          inputProps={{ step: 0.1, style: { fontFamily: "monospace" } }}
        />

        <TextField
          label="Manual Peak Pressure Measurement Value"
          placeholder="e.g. 155"
          type="number"
          value={manualValue}
          onChange={(e) => setManualValue(e.target.value)}
          size="small"
          fullWidth
          inputProps={{ step: 0.1, style: { fontFamily: "monospace" } }}
        />
      </Box>

      <Divider sx={{ my: 2.5 }} />

      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 2,
          p: 2,
          borderRadius: 2,
          background: "#eef0fd",
          border: "1px solid #c9d0f7",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "primary.main",
              display: "block",
              mb: 0.75,
            }}
          >
            New Sensitivity Value
          </Typography>
          <OutputValueBox>{newSensitivity}</OutputValueBox>
        </Box>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          onClick={compute}
          sx={{ whiteSpace: "nowrap", fontWeight: 600, px: 3 }}
        >
          Compute →
        </Button>
      </Paper>
    </Box>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const ToolPage = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Tool Page
      </Typography>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: 2,
            background: "grey.50",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Phase Shift" />
            <Tab label="Peak Pressure" />
          </Tabs>
        </Box>
        <Box sx={{ p: 3 }}>
          <TabPanel value={activeTab} index={0}>
            <PhaseShiftContent />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <PeakPressureContent />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default ToolPage;
